import * as Parser from "papaparse";
import { get, uniqWith } from "lodash";
import { thor } from "../../../typings/thor";
import { ImportCsvBaseService } from "./import-csv-base.service";
import { PREMIUM_DESCRIPTION_MIN_LENGTH } from "../../../thor.const";
import { Events } from "../../../thor.enums";

enum CsvHeaders {
    OrderNbrPrimary = "orderNumberPrimary",
    OrderNbrSecondary = "orderNumberSecondary",
    Desc = "description",
    EstimMin = "estimateMin",
    EstimMax = "estimateMax"
}

export interface Row {
    orderNumberPrimary: number;
    orderNumberSecondary: string;
    description: string;
    estimateMin: number;
    estimateMax: number;
    line?: number;
}

interface Summary {
    created: number;
    updated: number;
    notModified: number;
    uncorrectRow: number;
    errors: SummaryLog[];
    warnings: SummaryLog[];
}

interface SummaryLog {
    row: Row;
    message: string;
    premium_error?: boolean;
}

export class ImportSaleItemsService extends ImportCsvBaseService {
    public static $inject: string[] = ["$q", "$rootScope", "$translate", "itemService", "utilsService"];

    protected csvHeaders: string[] = Object.values(CsvHeaders);
    protected summary: Summary;

    constructor(
        protected $q: ng.IQService,
        protected $rootScope: ng.IRootScopeService,
        $translate: ng.translate.ITranslateService,
        itemService,
        protected utils
    ) {
        super($q, $translate, itemService);
    }

    public engine(sale, file) {
        this.sale = sale;
        this.globalDeferred = this.$q.defer();
        this.summary = {
            created: 0,
            updated: 0,
            notModified: 0,
            uncorrectRow: 0,
            errors: [],
            warnings: []
        };

        const parseConfig: Parser.ParseConfig = {
            skipEmptyLines: false,
            dynamicTyping: true,
            header: true,
            encoding: "",
            delimiter: ((input) => this.guessDelimiter(input).bestColumnDelimiter) as unknown as string,
            complete: results => {
                if (results && results.data) {
                    this.engineRows(results.data)
                        .then(() => this.sendSummary())
                        .then(() => {
                            this.$rootScope.$broadcast(Events.PostMessageToV2);
                            return this.globalDeferred.resolve();
                        })
                        .catch(error => this.globalDeferred.reject(error));
                } else {
                    this.globalDeferred.reject();
                }
            },
            beforeFirstChunk: (chunk: string) => {
                const {bestLinebreak, bestColumnDelimiter } = this.guessDelimiter(chunk);
                const headers = this.csvHeaders.join(bestColumnDelimiter);
                return `${headers}${bestLinebreak}${chunk}`;
            },
            error: error => this.globalDeferred.reject(error)
        };

        this.utils.readFileAsString(file).then(str => {
            const detectedEncoding = this.utils.detectStringEncoding(str);
            if (detectedEncoding) {
                parseConfig.encoding = detectedEncoding;
            }
            Parser.parse(str, parseConfig);
        });

        return this.globalDeferred.promise;
    }

    protected engineRows(rows: Row[]) {
        this.notify("setGauge", { gauge: "rows", counter: "total", value: rows.length });

        let line = 1;
        rows = rows.map(row => {
            row.line = line++;
            return row;
        });

        const uniqRows = uniqWith(rows, (firstRow: Row, secondRow: Row) => {
            const firstNormalizedOrderNumber =
                (firstRow.orderNumberPrimary ? firstRow.orderNumberPrimary.toString() : "") +
                (firstRow.orderNumberSecondary ? `-${firstRow.orderNumberSecondary.toString()}` : "");
            const secondNormalizedOrderNumber =
                (secondRow.orderNumberPrimary ? secondRow.orderNumberPrimary.toString() : "") +
                (secondRow.orderNumberSecondary ? `-${secondRow.orderNumberSecondary.toString()}` : "");

            if (firstNormalizedOrderNumber === secondNormalizedOrderNumber) {
                this.summary.errors.push({
                    row: firstRow,
                    message: this.$t("ligne_avec_numero_d_ordre_doublon__cette_ligne_est_ignoree")
                });
            }

            return firstNormalizedOrderNumber === secondNormalizedOrderNumber;
        });

        return this.$q.series(uniqRows, this.engineRow.bind(this));
    }

    protected engineRow(row: Row) {
        const promises = [];

        if (!this.isCorrectRow(row)) {
            this.notify("incrementGauge", { gauge: "rows", counter: "error" });
            this.summary.uncorrectRow++;
            return this.$q.resolve();
        }

        const matchedItems = this.findMatchedItems(row);
        if (matchedItems.length > 0) {
            if (matchedItems.length > 1) {
                this.summary.warnings.push({
                    row,
                    message: this.$t("x_lots_mis_a_jour_pour_cette_ligne", { X: matchedItems.length })
                });
            }
            matchedItems.forEach(item => promises.push(this.updateItem(item, row)));
        } else {
            this.summary.warnings.push({
                row,
                message: this.$t("lot_introuvable_creation_d_un_lot")
            });
            promises.push(this.createItem(row));
        }

        return this.$q
            .all(promises)
            .then(() => this.notify("incrementGauge", { gauge: "rows", counter: "valid" }))
            .catch(() => {
                this.summary.errors.push({
                    row,
                    message: this.$t(
                        "erreur_a_l_enregistrement__veuillez_recommencer_l_operation_ou_contacter_le_support"
                    )
                });
                this.notify("incrementGauge", { gauge: "rows", counter: "error" });
            });
    }

    protected isCorrectRow(row: Row): boolean {
        return this.checkCorrectRow(row);
    }

    protected checkCorrectRow(row: Row) {
        if ((row.orderNumberPrimary as unknown) === "" && !row.orderNumberSecondary && !row.description) {
            this.summary.errors.push({ row, message: this.$t("ligne_vide") });
            return false;
        }

        // orderNumberPrimary must be a positive integer
        if (
            row.orderNumberPrimary === 0 ||
            row.orderNumberPrimary !== parseInt(row.orderNumberPrimary as unknown as string, 10)
        ) {
            this.summary.errors.push({ row, message: this.$t("numero_d_ordre_invalide") });
            return false;
        }

        // orderNumber must not already exist in suppressed items
        const findItemSuppressedOrderNumber = this.findItemSuppressedOrderNumber(row);
        if (findItemSuppressedOrderNumber) {
            this.summary.errors.push({
                row,
                message: this.$t("numero_d_ordre_x_deja_existant_dans_les_lots_supprimes", {
                    NUMBER: findItemSuppressedOrderNumber
                })
            });
            return false;
        }

        // description has to be a String
        if (typeof row.description !== "string") {
            this.summary.errors.push({ row, message: this.$t("aucune_description") });
            return false;
        }

        // Check Premium criteria
        if (this.isMatchedItemPremium(row) && row.description?.length < PREMIUM_DESCRIPTION_MIN_LENGTH) {
            this.summary.errors.push({ row, message: this.$t("refusal_reasons.description"), premium_error: true });
            return false;
        }

        const estimateMin = Number(row.estimateMin);
        const estimateMax = Number(row.estimateMax);

        // estimateMin is defined but invalid: value is not an integer or lower than 0
        if (row.estimateMin && (!Number.isInteger(estimateMin) || Math.sign(estimateMin) < 0)) {
            this.summary.errors.push({ row, message: this.$t("estimation_basse_invalide") });
            return false;
        }
        // estimateMax is defined but invalid: value is not an integer or lower than 0
        if (row.estimateMax && (!Number.isInteger(estimateMax) || Math.sign(estimateMax) < 0)) {
            this.summary.errors.push({ row, message: this.$t("estimation_haute_invalide") });
            return false;
        }
        // estimateMax lower than estimateMin
        if (estimateMin && estimateMax && estimateMin > estimateMax) {
            this.summary.errors.push({
                row,
                message: this.$t("mise_a_jour_impossible_l_estimation_basse_doit_etre_inferieure_a_l_estimation_haute")
            });
            return false;
        }

        return true;
    }

    protected findMatchedItems(row: Row): thor.ItemGet[] {
        const orderNumberPrimary = row.orderNumberPrimary;
        const orderNumberSecondary = row.orderNumberSecondary ? row.orderNumberSecondary.toString() : "";

        return this.sale.items.filter(item => {
            if (item.states.suppressed) {
                return false;
            }

            const itemOrderNumberPrimary = get(item, "meta.order_number.primary", 0) || 0;
            const itemOrderNumberSecondary = get(item, "meta.order_number.secondary", "") || "";

            if (itemOrderNumberPrimary === orderNumberPrimary && itemOrderNumberSecondary === orderNumberSecondary) {
                return true;
            }

            const medias = item.medias;
            if (!medias || !medias.length) {
                return false;
            }

            const filename = get(medias[0], "meta.name");
            if (!orderNumberSecondary) {
                const pattern = new RegExp(`^${orderNumberPrimary}(?:_\\d+)?\\.jpe?g`, "i");

                const match = pattern.exec(filename);
                if (match) {
                    return true;
                }
            }

            if (orderNumberSecondary) {
                const pattern = new RegExp(
                    `^${orderNumberPrimary}[\\-.\\s]${orderNumberSecondary}(?:_\\d+)?\\.jpe?g`,
                    "i"
                );
                const match = pattern.exec(filename);
                if (match) {
                    return true;
                }
            }

            return false;
        });
    }

    protected findItemSuppressedOrderNumber(row: Row): string {
        const rowOrderNumber = this.getRowOrderNbr(row);
        return this.sale.items.some(
            item => item.states.suppressed && rowOrderNumber === this.utils.getOrderNumberNormalized(item)
        )
            ? rowOrderNumber
            : undefined;
    }

    protected updateItem(item: thor.ItemGet, row: Row) {
        const itemEstimateMin = get(item, "pricing.estimates.min", 0) || 0;
        const itemEstimateMax = get(item, "pricing.estimates.max", 0) || 0;
        const rowEstimateMin = get(row, "estimateMin", 0) || 0;
        const rowEstimateMax = get(row, "estimateMax", 0) || 0;

        if (
            item.description === row.description &&
            itemEstimateMin === rowEstimateMin &&
            itemEstimateMax === rowEstimateMax
        ) {
            this.summary.notModified++;
            return this.$q.resolve();
        }

        return this.itemService.update(item.id, this.formatData(row)).then(() => this.summary.updated++);
    }

    protected createItem(row: Row) {
        const data = this.formatData(row);
        data.category_id = this.sale.resource.category_id;
        return this.sale.createItem(data).then(() => this.summary.created++);
    }

    protected formatData(row: Row) {
        const data: thor.ItemPost = {
            description: row.description,
            meta: {
                order_number: {
                    primary: row.orderNumberPrimary
                }
            },
            pricing: null
        };

        if (row.orderNumberSecondary) {
            data.meta.order_number.secondary = row.orderNumberSecondary.toString();
        }

        if (row.estimateMin || row.estimateMax) {
            data.pricing = { estimates: {} };

            if (row.estimateMin) {
                data.pricing.estimates.min = row.estimateMin;
            }

            if (row.estimateMax) {
                data.pricing.estimates.max = row.estimateMax;
            }
        }

        return data;
    }

    protected getRowOrderNbr(row: Row): string {
        return [row.orderNumberPrimary, row.orderNumberSecondary].filter(Boolean).join("-");
    }

    protected isMatchedItemPremium(row: Row): boolean {
        return !!this.sale.items.find(
            (item: thor.ItemGet) =>
                item.asked_for_premium && this.getRowOrderNbr(row) === this.utils.getOrderNumberNormalized(item)
        );
    }
}
